import { useState } from 'react';
import { Plus, Database, History, Cpu, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface GibletPlusMenuProps {
  connectedKBsCount: number;
  maxKBs: number;
  onOpenKBPanel: () => void;
  onOpenHistory: () => void;
  onOpenThreads: () => void;
  selectedBackend: 'lovable' | 'openai';
  onBackendChange: (backend: 'lovable' | 'openai') => void;
}

export const GibletPlusMenu = ({
  connectedKBsCount,
  maxKBs,
  onOpenKBPanel,
  onOpenHistory,
  onOpenThreads,
  selectedBackend,
  onBackendChange,
}: GibletPlusMenuProps) => {
  const [open, setOpen] = useState(false);

  return (
    <TooltipProvider delayDuration={300}>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-muted flex-shrink-0"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>More options</TooltipContent>
        </Tooltip>
      <PopoverContent 
        className="w-56 p-2" 
        side="top" 
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <TooltipProvider delayDuration={300}>
        <div className="flex flex-col gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-9 text-sm"
                onClick={() => {
                  onOpenKBPanel();
                  setOpen(false);
                }}
              >
                <Database className="h-4 w-4" />
                <span>Connect KBs</span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {connectedKBsCount}/{maxKBs}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Link knowledge bases to this conversation</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-9 text-sm"
                onClick={() => {
                  onOpenHistory();
                  setOpen(false);
                }}
              >
                <History className="h-4 w-4" />
                <span>View History</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">View past messages</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-9 text-sm"
                onClick={() => {
                  onOpenThreads();
                  setOpen(false);
                }}
              >
                <Layers className="h-4 w-4" />
                <span>Threads</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Switch between conversations</TooltipContent>
          </Tooltip>
          
          <div className="border-t my-1" />
          
          <div className="px-2 py-1.5">
            <div className="flex items-center gap-2 mb-1.5">
              <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">AI Model</span>
            </div>
            <Select
              value={selectedBackend}
              onValueChange={(value: 'lovable' | 'openai') => {
                onBackendChange(value);
                setOpen(false);
              }}
            >
              <SelectTrigger className="w-full h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lovable">
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-green-500">●</span>
                    Gemini 2.5 Flash
                  </span>
                </SelectItem>
                <SelectItem value="openai">
                  <span className="flex items-center gap-2 text-xs">
                    <span className="text-blue-500">●</span>
                    GPT-4o
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          </div>
        </TooltipProvider>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
};
